import React, { useEffect, useRef, useState } from "react";

import { ProgressBar, type ProgressBarProps } from "../../atoms/ProgressBar";

export type LoadingBarProps = ProgressBarProps & {
	timeToComplete: number;
	initialProgress?: number;
};

const LoadingBar = ({ timeToComplete, initialProgress = 0, value, ...props }: LoadingBarProps) => {
	const [progress, setProgress] = useState(initialProgress);
	const animationFrame = useRef<number | null>(null);

	useEffect(() => {
		const start = performance.now();
		const from = progress;
		const to = value;
		// Use absolute value to handle negative numbers
		const validTimeToComplete = Math.abs(timeToComplete) || 1;

		const step = (now: number) => {
			const elapsed = now - start;
			const progressFraction = Math.min(elapsed / validTimeToComplete, 1);
			const progressLeft = (to - from) * progressFraction;
			const newValue = from + progressLeft;
			setProgress(newValue);
			if (progressFraction < 1) animationFrame.current = requestAnimationFrame(step);
		};

		animationFrame.current = requestAnimationFrame(step);
		return () => {
			if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, timeToComplete]);

	return <ProgressBar {...props} value={progress} />;
};

LoadingBar.displayName = "LoadingBar";

export { LoadingBar };
